import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import {
  type RouteProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { type NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Tabs, theme, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
  SignedOutView,
} from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { type AccountTabStackParamList } from '../../navigation/AppNavigation.props';
import { useOrdersTabs } from './hooks';
import { ordersMessages as messages } from './OrdersScreen.messages';
import { DigitalOrders, GiftCardOrders, Orders } from './tabs';

// ─────────────────────────────────────────────────────────────────────────────

export const OrdersScreen = () => {
  const isLoggedIn = useIsLoggedIn();

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) return <SignedOutView />;

  return <OrdersScreenLoggedIn />;
};

// ─────────────────────────────────────────────────────────────────────────────

export const OrdersScreenLoggedIn = () => {
  const isInStoreLoyaltyOrdersEnabled = useFeatureFlag(
    'CELS-4174-in-store-loyalty-orders-enabled',
  );

  // ─── Navigation ──────────────────────────────────────────────────────

  const { setParams } = useNavigation<NavigationProps>();
  const { params } = useRoute<RouteProp<AccountTabStackParamList, 'Orders'>>();
  const tabIdParam = params?.tabId;

  const { match, currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  // ─── Tabs ────────────────────────────────────────────────────────────

  const { activeTabItemId, setActiveTabItemId, ordersTab, giftCardsTab, tabs } =
    useOrdersTabs({ tabIdParam });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onTabChange = useCallback(
    (tabId: string) => {
      const castedTabId = tabId as OrderTabId;

      setActiveTabItemId(tabId);
      setParams({ tabId: castedTabId });
    },
    [setActiveTabItemId, setParams],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const tabsBarStyle = [
    styles.screenInnerContainer,
    match([styles.tabsContainerXS, styles.tabsContainerSM]),
  ];

  const withScrollWrapper = isInStoreLoyaltyOrdersEnabled
    ? currentBreakpoint.isXS
    : true;

  const removeContainerSpacing = isInStoreLoyaltyOrdersEnabled
    ? currentBreakpoint.isXS
    : false;

  const headerHorizontalSpacing = isInStoreLoyaltyOrdersEnabled
    ? match([styles.headerSpacingXS, null])
    : false;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer
      screen="orders"
      removeSpacing={removeContainerSpacing}
      withScrollWrapper={withScrollWrapper}
    >
      <View style={headerHorizontalSpacing}>
        <AccountScreenTitle title={formatMessage(messages.screenTitle)} />

        <View style={tabsBarStyle}>
          <Tabs.Bar
            palette="primary"
            tabs={tabs}
            activeTabId={activeTabItemId}
            onChange={onTabChange}
          />
        </View>
      </View>

      <View style={styles.screenInnerContainer}>
        <AccountScreenSection style={styles.content}>
          <Tabs.Panel
            isActive={activeTabItemId === ordersTab.id}
            tabId={ordersTab.id}
            panelId={ordersTab.panelId}
            style={styles.content}
          >
            {isInStoreLoyaltyOrdersEnabled ? <DigitalOrders /> : <Orders />}
          </Tabs.Panel>

          <Tabs.Panel
            isActive={activeTabItemId === giftCardsTab.id}
            tabId={giftCardsTab.id}
            panelId={giftCardsTab.panelId}
            style={styles.content}
          >
            <GiftCardOrders />
          </Tabs.Panel>
        </AccountScreenSection>
      </View>
    </AccountScreenContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flex: 1,
    maxWidth: '100%',
  },
  screenInnerContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 744,
  },
  tabsContainerXS: {
    marginBottom: theme.spacing['4'],
  },
  tabsContainerSM: {
    marginVertical: theme.spacing['6'],
  },
  headerSpacingXS: {
    marginHorizontal: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderTabId = NonNullable<AccountTabStackParamList['Orders']>['tabId'];
type NavigationProps = NativeStackNavigationProp<
  AccountTabStackParamList,
  'Orders'
>;
